import { client } from '@api/client';

import { normalizeObject } from '@utils/common';

import { IAssetTypeSubtypeResponse, IVisibleShowHideAssetsResponse, TAsset } from '@interfaces/assets/asset';
import {
	IEditAttributeDTO,
	IGetAssetsListDropdownDTO,
	TAssetCheckInformationDTO,
	TAssetFormDTO,
	TAssetsCommonValuesDTO,
	TAssetsListDropdownResponse,
	TAssetVisibilityDTO,
	TGetAssetsDTO,
} from '@interfaces/assets/dto/assetsDTO';
import { IGroupOperationDTO } from '@interfaces/common/defaultDTO';
import { IItemsResponse } from '@interfaces/common/response';
import { IContactPersons, TResponsible } from '@interfaces/components/form';
import { ISystemFields } from '@interfaces/import/import';
import {
	IAcquireAssetDTO,
	IAcquireMultipleAssetDTO,
	ISendQueryAssetDTO,
} from '@interfaces/map/dto/mapDTO';
import { IAcquirePolygonAssetDTO } from '@interfaces/map/dto/polygonDTO';

import { URL } from './urls';

const {
	assetAccessAvailability,
	assetCreate,
	assetManagement,
	assetManagementCommonValues,
	assetSiteAvailability,
	assetSpecialImplementationIssues,
	assetSubTypes,
	assetGroupShowHide,
	assetTypes,
	assetsTable,
	assetsIsVisibleShowHide,
	backhaulAccessibility,
	cart,
	checkAssetInformation,
	contactPersons,
	deleteAssets,
	powerSupplyAvailability,
	queryAsset,
	responsible,
	getAssetListDropdown,
} = URL;

export const updateAsset = ({ id, data }: { id: string; data: TAssetFormDTO }): Promise<void> => (
	client.post(`/${assetManagement}/${id}/update`, { ...data })
);

export const enableAsset = (id: string): Promise<void> => client.post(`/${assetManagement}/${id}/enable`, {});

export const disableAsset = (id: string): Promise<void> => client.post(`/${assetManagement}/${id}/disable`, {});

export const editBackhaulAccessibility = ({
	id,
	status,
	details,
	filesIds,
}: IEditAttributeDTO): Promise<void> =>
	client.post(`/${assetManagement}/${id}/${backhaulAccessibility}`, {
		backhaulAccessibilityDetails: details,
		backhaulAccessibilityStatus: status,
		backhaulAccessibilityDetailsFileIds: filesIds,
	});

export const editPowerSupplyAvailability = ({
	id,
	status,
	details,
	filesIds,
}: IEditAttributeDTO): Promise<void> =>
	client.post(`/${assetManagement}/${id}/${powerSupplyAvailability}`, {
		powerSupplyAvailabilityDetails: details,
		powerSupplyAvailabilityStatus: status,
		powerSupplyAvailabilityDetailsFileIds: filesIds,
	});

export const editAssetSiteAvailability = ({
	id,
	status,
	details,
	filesIds,
}: IEditAttributeDTO): Promise<void> =>
	client.post(`/${assetManagement}/${id}/${assetSiteAvailability}`, {
		assetSiteAvailabilityDetails: details,
		assetSiteAvailabilityStatus: status,
		assetSiteAvailabilityDetailsFileIds: filesIds,
	});

export const editAssetAccessAvailability = ({
	id,
	status,
	details,
	filesIds,
}: IEditAttributeDTO): Promise<void> =>
	client.post(`/${assetManagement}/${id}/${assetAccessAvailability}`, {
		assetAccessDetails: details,
		isAvailable: status,
		assetAccessDetailsFileIds: filesIds,
	});

export const editAssetSpecialImplementationIssues = ({
	id,
	status,
	details,
	filesIds,
}: IEditAttributeDTO): Promise<void> =>
	client.post(`/${assetManagement}/${id}/${assetSpecialImplementationIssues}`, {
		specialImplementationIssueDetails: details,
		specialImplementationIssuesExist: status,
		specialImplementationIssueDetailsFileIds: filesIds,
	});

export const removeAssets = ({
	filter,
	ids,
	isAll,
}: IGroupOperationDTO<TGetAssetsDTO | null>): Promise<void> => {
	const {
		assetSiteAvailabilityStatus,
		assetTypeId,
		assetSubTypeId,
		backhaulAccessibilityStatus,
		contactPersonId,
		createdAt,
		isAvailable,
		lastVerificationDate,
		page,
		pageSize,
		powerSupplyAvailabilityStatus,
		searchValue,
		siteAvailabilityReviewDate,
		sortDirection,
		sortFieldName,
		updatedAt,
		rentStatus,
	} = filter || {};

	return client.post(`/${deleteAssets}`, {
		filter: {
			assetSiteAvailabilityStatus,
			assetTypeId,
			assetSubTypeId,
			contactPersonId,
			backhaulAccessibilityStatus,
			isAvailable,
			page,
			pageSize,
			powerSupplyAvailabilityStatus,
			searchValue,
			sortDirection,
			sortFieldName,
			'createdAt.from': createdAt?.from,
			'createdAt.to': createdAt?.to,
			'updatedAt.from': updatedAt?.from,
			'updatedAt.to': updatedAt?.to,
			'lastVerificationDate.from': lastVerificationDate?.from,
			'lastVerificationDate.to': lastVerificationDate?.to,
			'siteAvailabilityReviewDate.from': siteAvailabilityReviewDate?.from,
			'siteAvailabilityReviewDate.to': siteAvailabilityReviewDate?.to,
			rentStatus,
		},
		ids,
		isAll,
	});
};

export const getAssets = ({
	assetSiteAvailabilityStatus,
	assetTypeId,
	assetSubTypeId,
	backhaulAccessibilityStatus,
	contactPersonId,
	createdAt,
	isAvailable,
	lastVerificationDate,
	page,
	pageSize,
	powerSupplyAvailabilityStatus,
	searchValue,
	siteAvailabilityReviewDate,
	sortDirection,
	sortFieldName,
	updatedAt,
	rentStatus,
	organizationId,
}: TGetAssetsDTO): Promise<IItemsResponse<TAsset>> => {
	return client.get(`/${assetsTable}`, {
		params: {
			assetSiteAvailabilityStatus,
			assetTypeId,
			assetSubTypeId,
			contactPersonId,
			backhaulAccessibilityStatus,
			isAvailable,
			page,
			pageSize,
			powerSupplyAvailabilityStatus,
			searchValue,
			sortDirection,
			sortFieldName,
			'createdAt.from': createdAt?.from,
			'createdAt.to': createdAt?.to,
			'updatedAt.from': updatedAt?.from,
			'updatedAt.to': updatedAt?.to,
			'lastVerificationDate.from': lastVerificationDate?.from,
			'lastVerificationDate.to': lastVerificationDate?.to,
			'siteAvailabilityReviewDate.from': siteAvailabilityReviewDate?.from,
			'siteAvailabilityReviewDate.to': siteAvailabilityReviewDate?.to,
			rentStatus,
			organizationId,
		},
	});
};

export const getIsVisibleShowHideAssets = (params?: TGetAssetsDTO): Promise<IVisibleShowHideAssetsResponse> => {
	const {
		assetSiteAvailabilityStatus,
		assetTypeId,
		assetSubTypeId,
		backhaulAccessibilityStatus,
		contactPersonId,
		createdAt,
		isAvailable,
		lastVerificationDate,
		powerSupplyAvailabilityStatus,
		searchValue,
		siteAvailabilityReviewDate,
		updatedAt,
		rentStatus,
		organizationId,
	} = params || {};

	return client.get(`/${assetsIsVisibleShowHide}`, {
		params: {
			'filter.assetSiteAvailabilityStatus': assetSiteAvailabilityStatus,
			'filter.assetTypeId': assetTypeId,
			'filter.assetSubTypeId': assetSubTypeId,
			'filter.contactPersonId': contactPersonId,
			'filter.backhaulAccessibilityStatus': backhaulAccessibilityStatus,
			'filter.isAvailable': isAvailable,
			'filter.powerSupplyAvailabilityStatus': powerSupplyAvailabilityStatus,
			'filter.searchValue': searchValue,
			'filter.createdAt.from': createdAt?.from,
			'filter.createdAt.to': createdAt?.to,
			'filter.updatedAt.from': updatedAt?.from,
			'filter.updatedAt.to': updatedAt?.to,
			'filter.lastVerificationDate.from': lastVerificationDate?.from,
			'filter.lastVerificationDate.to': lastVerificationDate?.to,
			'filter.siteAvailabilityReviewDate.from': siteAvailabilityReviewDate?.from,
			'filter.siteAvailabilityReviewDate.to': siteAvailabilityReviewDate?.to,
			'filter.rentStatus': rentStatus,
			isAll: true,
			organizationId,
		},
	});
};

export const getTypes = (): Promise<IAssetTypeSubtypeResponse[]> => {
	return client.get(`/${assetTypes}`);
};

export const getSubTypes = (): Promise<IAssetTypeSubtypeResponse[]> => client.get(`/${assetSubTypes}`);

export const getResponsible = (): Promise<TResponsible[]> => client.get(`/${responsible}`);

export const getContactPersons = (): Promise<IContactPersons[]> => {
	return client.get(`/${contactPersons}`);
};

export const assetCheckInformation = ({
	assetId,
	id,
}: TAssetCheckInformationDTO): Promise<void> => {
	return client.post(`/${checkAssetInformation}`, { assetId, id });
};

export const createAsset = (data: TAssetFormDTO): Promise<void> => client.post(`/${assetCreate}`, data);

export const findAssetsByAssetId = (
	searchValue?: string,
): Promise<IItemsResponse<TAsset>> => {
	return client.get(`/${assetManagement}/find`, {
		params: {
			searchValue,
			isOwnOnly: false,
		},
	});
};

export const sendQueryAsset = (dto: ISendQueryAssetDTO): Promise<void> => client.put(`/${queryAsset}`, dto);

export const checkAcquireMultipleAsset = (
	dto: IAcquireMultipleAssetDTO,
): Promise<void> => client.post(`/${cart}/check-add-to-cart`,
	dto,
);

export const checkAcquirePolygonAsset = (
	dto: IAcquirePolygonAssetDTO,
): Promise<void> => {
	return client.post(`/${cart}/check-add-to-cart/polygon`, dto);
};

export const sendAcquireAsset = (
	dto: IAcquireAssetDTO,
): Promise<void> => {
	return client.post(`/${cart}/${dto.assetDbId}/add-to-cart`,
		normalizeObject(dto),
	);
};

export const sendAcquireMultipleAsset = (
	dto: IAcquireMultipleAssetDTO,
): Promise<void> => client.post(`/${cart}/add-to-cart`,
	normalizeObject(dto),
);

export const sendAcquirePolygonAsset = (
	dto: IAcquirePolygonAssetDTO,
): Promise<void> => client.post(`/${cart}/add-to-cart/polygon`,
	normalizeObject(dto),
);

export const getAssetsListDropdown = (
	params: IGetAssetsListDropdownDTO,
): Promise<TAssetsListDropdownResponse> => {
	return client.get(getAssetListDropdown, {
		params: {
			...params,
		},
	});
};

export const showAssets = (dto: TAssetVisibilityDTO): Promise<void> => {
	return client.post(`/${assetGroupShowHide}/show`, dto);
};

export const hideAssets = (dto: TAssetVisibilityDTO): Promise<void> => {
	return client.post(`/${assetGroupShowHide}/hide`, dto);
};

export const getAssetsCommonValuesSystemFields = (): Promise<ISystemFields[]> => {
	return client.get(`/${assetManagementCommonValues}/get-system-fields`);
};

export const editAssetsCommonValues = (dto: TAssetsCommonValuesDTO): Promise<void> => {
	return client.post(assetManagementCommonValues, dto);
};
